import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";

export default function PrivacyPolicy() {
  const [s, setS] = useState({ support_email: "support@genuine.software", site_name: "genuine.software" });
  useEffect(() => {
    api.get("/settings/public").then((r) => setS((p) => ({ ...p, ...r.data }))).catch(() => {});
  }, []);
  const updated = "May 1, 2026";
  const email = s.support_email || "support@genuine.software";

  return (
    <div data-testid="privacy-policy-page" className="max-w-4xl mx-auto px-6 py-16">
      <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// legal</div>
      <h1 className="font-heading font-bold text-4xl md:text-5xl mt-2 leading-tight">Privacy Policy</h1>
      <p className="text-[#71717A] mt-3 font-mono text-xs">Last updated: {updated}</p>

      <div className="prose-legal mt-10 space-y-8 text-[#D4D4D8] leading-relaxed text-[15px]">
        <Section title="1. Introduction">
          <p>
            genuine.software ("we", "us", or "our") operates the website at the domain hosting this
            page and provides retail digital software licenses (Windows, Office, antivirus, Autodesk,
            and similar) directly to end users worldwide. This Privacy Policy describes what
            information we collect, how we use it, the parties we share it with, the security
            practices we apply, and the rights you have over your personal data.
          </p>
          <p>
            By using our website, placing an order, or contacting us, you acknowledge and accept
            this policy. If you do not agree, please do not use our services.
          </p>
        </Section>

        <Section title="2. Information we collect">
          <p>We collect only the minimum information needed to fulfill your order and protect our service:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Order information:</strong> email address (required to deliver license keys), purchased products, quantity, and order total.</li>
            <li><strong>Payment information:</strong> processed entirely by <strong>Stripe, Inc.</strong> We never see, store, or transmit your card number, CVC, expiry, or banking credentials. Stripe returns to us only a non-sensitive payment confirmation token and the last-4 digits / brand of your card for receipt purposes.</li>
            <li><strong>License delivery data:</strong> for subscription products that activate on your account (e.g. Autodesk), we store the activation email you provide so we can complete fulfillment.</li>
            <li><strong>Technical data:</strong> IP address, browser user-agent, request timestamps, and Cloudflare Turnstile challenge tokens — used solely for fraud prevention, abuse detection, and order audit logging.</li>
            <li><strong>Communications:</strong> any messages you send us via email, WhatsApp, iMessage, or our contact form.</li>
            <li><strong>Cookies:</strong> a single session cookie used to maintain your shopping cart. We do not use advertising cookies, behavioural-tracking cookies, or third-party analytics that profile you.</li>
          </ul>
          <p>We do <strong>not</strong> collect: your full name, physical address, phone number, date of birth, government IDs, or any special-category personal data.</p>
        </Section>

        <Section title="3. How we use your information">
          <ul className="list-disc pl-6 space-y-2">
            <li>To deliver the license keys you purchased to the email address you provide.</li>
            <li>To process payments via Stripe and issue digital receipts.</li>
            <li>To respond to your support, refund, or account-related inquiries.</li>
            <li>To prevent fraud, chargebacks, and abuse of our service (legitimate interest).</li>
            <li>To comply with applicable legal, tax, and accounting obligations.</li>
            <li>To send transactional notifications about your order (confirmation, delivery). We do not send marketing email unless you have explicitly subscribed.</li>
          </ul>
        </Section>

        <Section title="4. Legal basis (GDPR / UK GDPR)">
          <p>For visitors in the European Economic Area, United Kingdom, and similar jurisdictions, we rely on the following legal bases:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Performance of a contract</strong> — to fulfill the order you placed with us.</li>
            <li><strong>Legitimate interests</strong> — to protect our service from fraud and abuse, and to keep audit records.</li>
            <li><strong>Legal obligation</strong> — to retain records for tax, accounting, and dispute resolution.</li>
            <li><strong>Consent</strong> — only where you have explicitly opted in (e.g. optional marketing).</li>
          </ul>
        </Section>

        <Section title="5. Parties we share data with">
          <p>We share the minimum amount of data with the following service providers, strictly for the purpose stated:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Stripe, Inc.</strong> — payment processing. Stripe is PCI-DSS Level 1 certified. See <a className="text-[#00FF66] hover:underline" href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a>.</li>
            <li><strong>Cloudflare, Inc.</strong> — Turnstile bot-protection (CAPTCHA) at checkout. See <a className="text-[#00FF66] hover:underline" href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">cloudflare.com/privacypolicy</a>.</li>
            <li><strong>Microsoft (Office 365 SMTP)</strong> — outbound transactional email delivery for order receipts.</li>
            <li><strong>Hosting provider</strong> — server infrastructure that hosts our website and database.</li>
          </ul>
          <p>We do <strong>not</strong> sell, rent, or trade your personal data to any third party for marketing, advertising, or profiling. We may disclose information when compelled by valid legal process (e.g. a subpoena or court order) or to investigate suspected fraud.</p>
        </Section>

        <Section title="6. International data transfers">
          <p>
            Our service providers (Stripe, Cloudflare, Microsoft) operate globally and may process
            your data in the United States, the European Union, and other regions. They each
            maintain Standard Contractual Clauses, the EU–US Data Privacy Framework, or equivalent
            transfer mechanisms to ensure your data is protected to the same standard as your home
            jurisdiction.
          </p>
        </Section>

        <Section title="7. Data retention">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Order records (email, items, totals, license keys delivered):</strong> retained for up to 7 years to comply with tax, accounting, and dispute-resolution obligations.</li>
            <li><strong>Technical logs (IP, user-agent):</strong> retained up to 12 months for fraud and abuse investigation.</li>
            <li><strong>Support correspondence:</strong> retained up to 24 months after the issue is resolved.</li>
            <li><strong>Cart cookie:</strong> stored locally in your browser; cleared when you close it or complete checkout.</li>
          </ul>
        </Section>

        <Section title="8. Security">
          <p>We apply industry-standard practices to safeguard the information you entrust to us:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>TLS 1.2+ HTTPS encryption on every page of the website.</li>
            <li>All payment data is tokenised and processed by Stripe; we never receive raw card numbers.</li>
            <li>Database access is restricted to a small number of authorised administrators using strong passwords and rate-limited authentication.</li>
            <li>Administrative access is gated behind a parameterised URL, password, and JWT session.</li>
            <li>Cloudflare Turnstile protects checkout against automated abuse.</li>
            <li>Backups are performed regularly and stored encrypted at rest.</li>
            <li>We follow the principle of least privilege and the principle of data minimisation.</li>
          </ul>
          <p>No internet service can be 100% secure. If we ever discover a breach affecting your data, we will notify you and the relevant authorities within 72 hours, as required by law.</p>
        </Section>

        <Section title="9. Your rights">
          <p>Depending on your jurisdiction, you have the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>access the personal data we hold about you;</li>
            <li>request correction of inaccurate data;</li>
            <li>request deletion of your data ("right to be forgotten"), subject to legal retention obligations;</li>
            <li>request restriction of processing;</li>
            <li>request portability of your data in a machine-readable format;</li>
            <li>object to processing based on legitimate interests;</li>
            <li>withdraw consent at any time where consent is the legal basis;</li>
            <li>lodge a complaint with your local data-protection authority.</li>
          </ul>
          <p>To exercise any of these rights, email <a className="text-[#00FF66] hover:underline" href={`mailto:${email}`}>{email}</a>. We respond within 30 days.</p>
        </Section>

        <Section title="10. Children">
          <p>
            Our service is not directed to children under 16. We do not knowingly collect personal
            data from minors. If you believe a minor has provided us with information, contact us
            and we will delete the record.
          </p>
        </Section>

        <Section title="11. California (CCPA / CPRA) notice">
          <p>
            California residents have the right to know what personal information is collected,
            used, shared or sold, and the right to delete personal information. We do not sell or
            share personal information for cross-context behavioural advertising. To exercise your
            CCPA rights, email <a className="text-[#00FF66] hover:underline" href={`mailto:${email}`}>{email}</a>.
          </p>
        </Section>

        <Section title="12. Changes to this policy">
          <p>
            We may update this policy from time to time. The "Last updated" date at the top reflects
            the most recent revision. Material changes will be highlighted on the homepage banner
            for at least 30 days.
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            For privacy questions, requests, or complaints, please write to{" "}
            <a className="text-[#00FF66] hover:underline" href={`mailto:${email}`}>{email}</a>.
            See also our <Link className="text-[#00FF66] hover:underline" to="/terms-of-service">Terms of Service</Link>.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="surface p-6 md:p-7 border border-white/5">
      <h2 className="font-heading font-semibold text-xl text-white mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
